import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select } from 'antd';

import {
  AfterHours,
  MONTH_NAMES,
} from '@totem/components/portfolio/buildings/hvac/afterHours/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const styles = {
  row: {
    display: 'flex',
    gap: '4%',
  },
  halfWidth: {
    paddingBottom: '0',
    marginBottom: '1rem',
    width: '48%',
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
    width: '100%',
  },
};

type FormState = {
  year: number;
  month: number;
  hoursRequested: number;
  hoursBilled: number;
  billingRate: number;
  revenue: number;
  notes: string;
};

const currentYear = new Date().getFullYear();

const emptyForm = (): FormState => ({
  year: currentYear,
  month: new Date().getMonth() + 1,
  hoursRequested: 0,
  hoursBilled: 0,
  billingRate: 0,
  revenue: 0,
  notes: '',
});

type Props = {
  open: boolean;
  buildingId: string;
  spaceId: string;
  record?: AfterHours;
  onClose: () => void;
  onSaved: () => void;
};

const AfterHoursModal = ({
  open,
  buildingId,
  spaceId,
  record,
  onClose,
  onSaved,
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [isSending, setIsSending] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());

  const isEditing = !!record;

  useEffect(() => {
    if (open) {
      setForm(
        record
          ? {
              year: record.year,
              month: record.month,
              hoursRequested: record.hoursRequested,
              hoursBilled: record.hoursBilled,
              billingRate: record.billingRate,
              revenue: record.revenue,
              notes: record.notes ?? '',
            }
          : emptyForm(),
      );
    }
  }, [open, record]);

  const recalcRevenue = (hoursBilled: number, billingRate: number) => {
    const calculated = parseFloat((hoursBilled * billingRate).toFixed(2));
    setForm((prev) => ({
      ...prev,
      hoursBilled,
      billingRate,
      revenue: calculated,
    }));
  };

  const handleSubmit = useCallback(() => {
    if (isSending) {
      return;
    }

    setIsSending(true);

    const url = isEditing
      ? `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/after-hours/${record.id}`
      : `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/hvac-spaces/${spaceId}/after-hours`;

    const method = isEditing ? 'PATCH' : 'POST';

    fetch(url, {
      method,
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(form),
    })
      .then((res) => {
        CheckResponseShowError(res);

        onSaved();

        onClose();
      })
      .finally(() => setIsSending(false));
  }, [
    form,
    isSending,
    isEditing,
    buildingId,
    spaceId,
    record,
    getApiToken,
    onSaved,
    onClose,
  ]);

  const yearOptions = Array.from({ length: 10 }, (_, idx) => currentYear - idx);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={480}
      title={
        <ModalTitle>
          {isEditing ? 'Edit After-Hours Record' : 'Add After-Hours Record'}
        </ModalTitle>
      }
      footer={
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={isSending} onClick={handleSubmit}>
            {isEditing ? 'Save' : 'Add'}
          </Button>
        </ModalFooter>
      }
    >
      <Form layout="vertical">
        <div style={styles.row}>
          <Form.Item label="Year" style={styles.halfWidth} required>
            <Select
              value={form.year}
              onChange={(val) => setForm({ ...form, year: val })}
              style={{ width: '100%' }}
            >
              {yearOptions.map((yr) => (
                <Select.Option key={yr} value={yr}>
                  {yr}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Month" style={styles.halfWidth} required>
            <Select
              value={form.month}
              onChange={(val) => setForm({ ...form, month: val })}
              style={{ width: '100%' }}
            >
              {MONTH_NAMES.map((name, idx) => (
                <Select.Option key={idx + 1} value={idx + 1}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div style={styles.row}>
          <Form.Item label="Hours Requested" style={styles.halfWidth}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              value={form.hoursRequested}
              onChange={(val) => setForm({ ...form, hoursRequested: val ?? 0 })}
            />
          </Form.Item>
          <Form.Item label="Hours Billed" style={styles.halfWidth}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              value={form.hoursBilled}
              onChange={(val) => recalcRevenue(val ?? 0, form.billingRate)}
            />
          </Form.Item>
        </div>

        <div style={styles.row}>
          <Form.Item label="Billing Rate ($/hr)" style={styles.halfWidth}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={4}
              value={form.billingRate}
              onChange={(val) => recalcRevenue(form.hoursBilled, val ?? 0)}
            />
          </Form.Item>
          <Form.Item label="Revenue ($)" style={styles.halfWidth}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              value={form.revenue}
              onChange={(val) => setForm({ ...form, revenue: val ?? 0 })}
            />
          </Form.Item>
        </div>

        <Form.Item label="Notes" style={styles.formItem}>
          <Input.TextArea
            rows={2}
            value={form.notes}
            onChange={(evt) => setForm({ ...form, notes: evt.target.value })}
            placeholder="Optional"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AfterHoursModal;
